import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { HelpButton } from '@/components/HelpButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, TrendingUp, UserPlus, Loader2, Copy, QrCode, Info, ExternalLink, Building2, Plus, X, Crown, Check, Wallet, ArrowUpRight, ArrowDownLeft, BookOpen, HandCoins, Rocket, Send } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { ClickAnalyticsCard } from '@/components/ClickAnalyticsCard';
import { PartnerGuide } from '@/components/PartnerGuide';
import { PartnerSponsorshipsTab } from '@/components/PartnerSponsorshipsTab';
import { PartnerImplantClientsTab } from '@/components/PartnerImplantClientsTab';
import { BatchImplantCard } from '@/components/BatchImplantCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const PLAN_COLORS = {
  'Ativo': 'bg-emerald-100 text-emerald-700',
  'Grátis': 'bg-slate-100 text-slate-500',
  'Inativo': 'bg-red-100 text-red-600',
};

const PartnerPanel = () => {
  const { token } = useParams();
  // Lê query params ?tab=patrocinios&view=guia para deep-link do admin
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get('tab') || 'dashboard';
  const initialView = urlParams.get('view'); // 'guia' | null

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [affiliates, setAffiliates] = useState([]);
  const [loadingAffs, setLoadingAffs] = useState(false);
  const [showAffForm, setShowAffForm] = useState(false);
  const [creatingAff, setCreatingAff] = useState(false);
  const [affForm, setAffForm] = useState({ name: '', email: '', password: '', whatsapp: '', cpf_cnpj: '', pix_key: '', affiliate_type: 'padrão' });
  const [statement, setStatement] = useState(null);
  const [loadingStatement, setLoadingStatement] = useState(false);

  const fetchPanel = useCallback(async () => {
    try {
      const res = await fetch(`${API}/partners/panel/${token}`);
      if (!res.ok) throw new Error('Not found');
      const json = await res.json();
      setData(json);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAffiliates = useCallback(async () => {
    setLoadingAffs(true);
    try {
      const res = await fetch(`${API}/partners/panel/${token}/affiliates`);
      if (res.ok) {
        const json = await res.json();
        setAffiliates(json.affiliates || []);
      }
    } catch { /* silent */ }
    finally { setLoadingAffs(false); }
  }, [token]);

  const fetchStatement = useCallback(async () => {
    setLoadingStatement(true);
    try {
      const res = await fetch(`${API}/partners/panel/${token}/statement`);
      if (res.ok) {
        const json = await res.json();
        setStatement(json);
      }
    } catch { /* silent */ }
    finally { setLoadingStatement(false); }
  }, [token]);

  useEffect(() => { fetchPanel(); }, [fetchPanel]);
  useEffect(() => { if (activeTab === 'equipe') fetchAffiliates(); }, [activeTab, fetchAffiliates]);
  useEffect(() => { if (activeTab === 'extrato') fetchStatement(); }, [activeTab, fetchStatement]);

  const handleCreateAffiliate = async () => {
    if (!affForm.name.trim() || !affForm.email.trim() || !affForm.password.trim() || !affForm.pix_key.trim()) {
      toast.error('Preencha Nome, Email, Senha e Chave Pix');
      return;
    }
    setCreatingAff(true);
    try {
      // Normaliza tipo antes de enviar (backend espera 'padrao' sem acento ou 'both')
      const payload = {
        ...affForm,
        affiliate_type: affForm.affiliate_type === 'both' ? 'both' : 'padrao',
      };
      const res = await fetch(`${API}/partners/panel/${token}/register-affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Erro');
      toast.success(json.message || 'Afiliado cadastrado!');
      setShowAffForm(false);
      setAffForm({ name: '', email: '', password: '', whatsapp: '', cpf_cnpj: '', pix_key: '', affiliate_type: 'padrão' });
      fetchAffiliates();
    } catch (e) {
      toast.error(e.message || 'Erro ao cadastrar');
    } finally { setCreatingAff(false); }
  };

  // Slug normalizado para UTM (nunca muda a rota, só rastreia no Admin Campaigns)
  const partnerUtm = data?.partner ? `?utm_source=parceiro&utm_medium=indicacao&utm_campaign=parceiro-${data.partner.slug}&utm_content=${encodeURIComponent(data.partner.name || data.partner.slug)}` : '';
  const buildSalesLink = () => `${window.location.origin}/parceiro/${data.partner.slug}${partnerUtm}`;

  const copyLink = () => {
    navigator.clipboard.writeText(buildSalesLink());
    toast.success('Link copiado! Já vem com rastreamento automático.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-6">
        <div className="text-center">
          <img src={LOGO_URL} alt="FaloComODono" className="h-14 mx-auto mb-6 object-contain" />
          <p className="text-white/60 text-lg">Painel não encontrado ou parceiro inativo.</p>
        </div>
      </div>
    );
  }

  const { partner, stats, clients } = data;

  return (
    <div className="min-h-screen bg-[#0f1b2d]" data-testid="partner-panel-page">
      {/* Header */}
      <header className="bg-[#0A2647] border-b border-white/10 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <img src={LOGO_URL} alt="FaloComODono" className="h-8 sm:h-9 object-contain flex-shrink-0" />
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <div className="min-w-0">
              <p className="text-white font-bold text-sm leading-tight truncate">{partner.name}</p>
              <p className="text-[#C5A059] text-[10px] font-semibold uppercase tracking-wider">Painel do Parceiro</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-[#C5A059]/10 border border-[#C5A059]/25 rounded-full px-2.5 sm:px-4 py-1 sm:py-1.5 flex-shrink-0">
            <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#C5A059]" />
            <span className="text-[11px] sm:text-xs font-semibold text-[#C5A059] whitespace-nowrap">{partner.commission_rate}%<span className="hidden sm:inline"> de comissão</span></span>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-[#0A2647]/50 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 flex gap-1 overflow-x-auto scrollbar-hide">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
            { id: 'extrato', label: 'Extrato', icon: Wallet },
            { id: 'equipe', label: 'Minha Equipe', icon: Users },
            { id: 'implantar', label: 'Implantar Cliente', icon: Rocket },
            { id: 'patrocinios', label: 'Patrocínios', icon: HandCoins },
            { id: 'guia', label: 'Guia', icon: BookOpen },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-[#C5A059] text-[#C5A059]'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`} data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'dashboard' && (
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="partner-panel-kpis">
          <Card className="border-0 bg-[#0A2647] shadow-lg">
            <CardContent className="pt-4 sm:pt-5 pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white truncate" data-testid="kpi-active-clients">{stats.active_clients}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">Clientes Ativos</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#0A2647] shadow-lg">
            <CardContent className="pt-4 sm:pt-5 pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-[#C5A059]" />
                </div>
                <div className="min-w-0">
                  <p className="text-base sm:text-2xl font-bold text-[#C5A059] truncate whitespace-nowrap" data-testid="kpi-monthly-recurrence">
                    R$ {stats.monthly_recurrence.toFixed(2).replace('.', ',')}
                  </p>
                  <p className="text-[10px] text-slate-400 leading-tight">Recorrência/Mês</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#0A2647] shadow-lg">
            <CardContent className="pt-4 sm:pt-5 pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white truncate" data-testid="kpi-new-month">{stats.new_this_month}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">Novos este Mês</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 bg-[#0A2647] shadow-lg">
            <CardContent className="pt-4 sm:pt-5 pb-3 sm:pb-4 px-3 sm:px-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xl sm:text-2xl font-bold text-white truncate" data-testid="kpi-total-clients">{stats.total_clients}</p>
                  <p className="text-[10px] text-slate-400 leading-tight">Total de Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Click Analytics */}
        <ClickAnalyticsCard
          endpoint={`/partners/panel/${token}/clicks`}
          title="Cliques da sua rede (30 dias)"
        />

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Client Table - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-4 min-w-0">
            <Card className="border-0 bg-[#0A2647] shadow-lg overflow-hidden" data-testid="partner-clients-table">
              <CardContent className="p-0">
                <div className="px-4 sm:px-5 py-4 border-b border-white/10">
                  <h2 className="text-white font-bold text-sm">Seus Clientes</h2>
                  <p className="text-slate-400 text-xs mt-0.5">{stats.active_clients} ativos de {stats.total_clients} total</p>
                </div>
                {clients.length === 0 ? (
                  <div className="py-12 text-center">
                    <Building2 className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Nenhum cliente ainda.</p>
                    <p className="text-slate-500 text-xs mt-1">Compartilhe seu link para começar a vender!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full min-w-[500px]">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 sm:px-5 py-3">Empresa</th>
                          <th className="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 sm:px-3 py-3">Plano</th>
                          <th className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-2 sm:px-3 py-3">Status</th>
                          <th className="text-right text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 sm:px-5 py-3">Comissão</th>
                          <th className="hidden sm:table-cell text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">Split</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((c, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
                            data-testid={`client-row-${i}`}>
                            <td className="px-3 sm:px-5 py-3">
                              <p className="text-white text-xs sm:text-sm font-medium truncate max-w-[140px] sm:max-w-[200px]">{c.name || 'Sem nome'}</p>
                            </td>
                            <td className="px-2 sm:px-3 py-3">
                              <span className="text-slate-300 text-[11px] sm:text-xs">{c.plan}</span>
                            </td>
                            <td className="px-2 sm:px-3 py-3 text-center">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${PLAN_COLORS[c.status] || 'bg-slate-100 text-slate-500'}`}>
                                {c.status}
                              </span>
                            </td>
                            <td className="px-3 sm:px-5 py-3 text-right">
                              <span className={`text-xs sm:text-sm font-bold whitespace-nowrap ${c.commission > 0 ? 'text-[#C5A059]' : 'text-slate-500'}`}>
                                R$ {c.commission.toFixed(2).replace('.', ',')}
                              </span>
                            </td>
                            <td className="hidden sm:table-cell px-3 py-3 text-center">
                              {c.split_info ? (
                                c.split_info.status === 'ativo' ? (
                                  <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">Ativo</span>
                                ) : (
                                  <div className="flex flex-col items-center gap-1">
                                    <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                      <div className="h-full bg-[#C5A059] rounded-full transition-all" style={{ width: `${c.split_info.percent}%` }} />
                                    </div>
                                    <span className="text-[9px] text-slate-500">{c.split_info.percent}%</span>
                                  </div>
                                )
                              ) : (
                                <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">Ativo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar: Link + QR + Asaas Info */}
          <div className="space-y-4">
            {/* Sales Link + QR Code */}
            <Card className="border-0 bg-[#0A2647] shadow-lg" data-testid="partner-panel-link-card">
              <CardContent className="p-5">
                <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-[#C5A059]" />
                  Seu Link de Vendas
                </h3>
                <div className="flex justify-center mb-4">
                  <div className="bg-white p-3 rounded-xl">
                    <QRCodeCanvas
                      id="partner-panel-qr"
                      value={buildSalesLink()}
                      size={140}
                      level="H"
                      marginSize={2}
                      fgColor="#0A2647"
                      bgColor="#FFFFFF"
                    />
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 mb-3">
                  <p className="text-[11px] font-mono text-slate-300 truncate" data-testid="partner-panel-link">
                    {window.location.origin}/parceiro/{partner.slug}
                  </p>
                  <p className="text-[10px] text-[#C5A059] mt-0.5 truncate">
                    +rastreamento automático
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={copyLink}
                    className="flex-1 h-9 text-xs font-semibold bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647]"
                    data-testid="partner-panel-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5 mr-1.5" />Copiar Link
                  </Button>
                  <Button
                    onClick={() => {
                      const msg = encodeURIComponent(`Conheca o Falo Com O Dono! Sistema de avaliações para seu negócio:\n\n${buildSalesLink()}`);
                      window.open(`https://wa.me/?text=${msg}`, '_blank');
                    }}
                    className="h-9 px-3 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="partner-panel-whatsapp-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <a href={buildSalesLink()} target="_blank" rel="noreferrer" className="block mt-2">
                  <Button className="w-full h-9 text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/10" data-testid="partner-panel-view-page-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Ver Pagina de Vendas
                  </Button>
                </a>
                <p className="text-[10px] text-slate-500 mt-2 text-center">
                  Compartilhe este link com empresas. Cada venda gera {partner.commission_rate}% de comissão recorrente.
                </p>
              </CardContent>
            </Card>

            {/* Asaas Withdrawal Instructions */}
            <Card className="border-0 bg-[#0A2647] shadow-lg" data-testid="partner-panel-asaas-info">
              <CardContent className="p-5">
                <div className="flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Info className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm mb-2">Como sacar seus ganhos?</h3>
                    <div className="space-y-2 text-[11px] text-slate-400 leading-relaxed">
                      <p>
                        <span className="text-white font-semibold">1.</span> Você recebeu um e-mail da <span className="text-blue-400 font-semibold">Asaas</span> para ativar sua conta.
                      </p>
                      <p>
                        <span className="text-white font-semibold">2.</span> Acesse{' '}
                        <a href="https://www.asaas.com" target="_blank" rel="noreferrer" className="text-blue-400 underline font-semibold">www.asaas.com</a>{' '}
                        com seu email e a senha que você definiu.
                      </p>
                      <p>
                        <span className="text-white font-semibold">3.</span> Em <span className="text-white font-semibold">"Financeiro"</span>, você vera os splits recebidos e pode solicitar <span className="text-[#C5A059] font-semibold">transferencia para sua conta bancaria</span>.
                      </p>
                    </div>
                    {!partner.wallet_id && (
                      <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                        <p className="text-[10px] text-amber-400 font-semibold">
                          Sua subconta Asaas ainda não esta configurada. Entre em contato com o administrador.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      )}

      {/* Extrato Tab */}
      {activeTab === 'extrato' && (
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {loadingStatement ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" /></div>
        ) : statement ? (
          <>
            {/* Balance Card */}
            <Card className="border-0 bg-[#0A2647] shadow-lg" data-testid="partner-balance-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Saldo a Receber</p>
                    <p className={`text-3xl font-bold ${statement.balance >= 0 ? 'text-[#C5A059]' : 'text-red-400'}`} data-testid="partner-balance">
                      R$ {statement.balance.toFixed(2).replace('.', ',')}
                    </p>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <p className="text-emerald-400 text-lg font-bold">R$ {statement.total_credits.toFixed(2).replace('.', ',')}</p>
                      <p className="text-[10px] text-slate-400">Total a Receber</p>
                    </div>
                    <div>
                      <p className="text-blue-400 text-lg font-bold">R$ {statement.total_debits.toFixed(2).replace('.', ',')}</p>
                      <p className="text-[10px] text-slate-400">Total Recebido</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transaction List */}
            <Card className="border-0 bg-[#0A2647] shadow-lg" data-testid="partner-transactions">
              <CardContent className="p-0">
                <div className="px-5 py-4 border-b border-white/10">
                  <h2 className="text-white font-bold text-sm">Extrato</h2>
                  <p className="text-slate-400 text-xs mt-0.5">{statement.transactions.length} lançamento(s)</p>
                </div>
                {statement.transactions.length === 0 ? (
                  <div className="py-12 text-center">
                    <Wallet className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Nenhum lançamento ainda.</p>
                    <p className="text-slate-500 text-xs mt-1">Aqui aparecerão suas comissões a receber e pagamentos recebidos.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {statement.transactions.map(t => (
                      <div key={t.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-white/[0.02] transition-colors" data-testid={`transaction-${t.id}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            t.type === 'credit' ? 'bg-emerald-500/15' : 'bg-blue-500/15'
                          }`}>
                            {t.type === 'credit'
                              ? <ArrowDownLeft className="w-4 h-4 text-emerald-400" />
                              : <ArrowUpRight className="w-4 h-4 text-blue-400" />
                            }
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{t.description}</p>
                            <p className="text-slate-500 text-[10px]">
                              {new Date(t.date).toLocaleDateString('pt-BR')} as {new Date(t.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-sm font-bold ${t.type === 'credit' ? 'text-emerald-400' : 'text-blue-400'}`}>
                            {t.type === 'credit' ? '+' : '-'} R$ {t.amount.toFixed(2).replace('.', ',')}
                          </p>
                          <p className="text-[10px] text-slate-500">Saldo: R$ {t.balance_after.toFixed(2).replace('.', ',')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </main>
      )}

      {/* Equipe Tab */}
      {activeTab === 'equipe' && (
      <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-lg">Minha Equipe</h2>
            <p className="text-slate-400 text-xs">{affiliates.length} afiliado(s) cadastrado(s)</p>
          </div>
          <Button onClick={() => setShowAffForm(true)}
            className="h-10 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] text-sm font-semibold gap-2"
            data-testid="partner-add-affiliate-btn">
            <Plus className="w-4 h-4" />Cadastrar Afiliado
          </Button>
        </div>

        {/* Link de Recrutamento de Afiliados */}
        <Card className="border-2 border-dashed border-[#C5A059]/50 bg-white/5" data-testid="partner-recruit-link-card">
          <CardContent className="p-4 space-y-2.5">
            <p className="text-sm font-bold text-white">⚡ Recrute afiliados sem digitar nada</p>
            <p className="text-xs text-slate-400 leading-relaxed">
              Envie este link: a pessoa se cadastra sozinha, é aprovada na hora, recebe o kit de vendas no WhatsApp
              e <span className="text-[#C5A059] font-semibold">já entra na sua equipe automaticamente</span>.
            </p>
            <p className="text-xs font-mono text-slate-300 bg-black/20 border border-white/10 rounded-lg px-3 py-2 break-all" data-testid="partner-recruit-link">
              {window.location.origin}/afiliado/cadastro?parceiro={partner.slug}
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] gap-1.5 text-xs h-8 px-3" data-testid="partner-recruit-copy"
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/afiliado/cadastro?parceiro=${partner.slug}`); toast.success('Link de recrutamento copiado'); }}>
                <Copy className="w-3 h-3" /> Copiar link
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 text-xs h-8 px-3 border-emerald-400/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="partner-recruit-whatsapp"
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Quer ganhar comissão indicando o Falo com o Dono? ⭐\n\nCadastro em 1 minuto, aprovação na hora e você já sai com seu link de vendas:\n\n${window.location.origin}/afiliado/cadastro?parceiro=${partner.slug}`)}`, '_blank')}>
                <Send className="w-3 h-3" /> Enviar no WhatsApp
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Register Form */}
        {showAffForm && (
          <Card className="border-2 border-[#C5A059]/30 bg-[#0A2647] shadow-lg" data-testid="partner-aff-form">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-sm">Novo Afiliado</h3>
                <button onClick={() => setShowAffForm(false)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[11px] text-slate-400">Nome Completo *</Label>
                  <Input value={affForm.name} onChange={e => setAffForm(f => ({ ...f, name: e.target.value }))}
                    className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-name" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">Email *</Label>
                  <Input type="email" value={affForm.email} onChange={e => setAffForm(f => ({ ...f, email: e.target.value }))}
                    className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-email" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">Senha *</Label>
                  <Input type="text" value={affForm.password} onChange={e => setAffForm(f => ({ ...f, password: e.target.value }))}
                    className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-password" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">WhatsApp</Label>
                  <Input value={affForm.whatsapp} onChange={e => setAffForm(f => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="5531999990000" className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-whatsapp" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">CPF/CNPJ</Label>
                  <Input value={affForm.cpf_cnpj} onChange={e => setAffForm(f => ({ ...f, cpf_cnpj: e.target.value }))}
                    className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-cpf" />
                </div>
                <div>
                  <Label className="text-[11px] text-slate-400">Chave Pix *</Label>
                  <Input value={affForm.pix_key} onChange={e => setAffForm(f => ({ ...f, pix_key: e.target.value }))}
                    placeholder="CPF, Email, Telefone ou Chave" className="mt-1 h-9 text-sm bg-white/5 border-white/10 text-white" data-testid="aff-pix" />
                </div>
              </div>
              <div className="mt-4 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-lg">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={affForm.affiliate_type === 'both' || affForm.affiliate_type === 'master'}
                    onChange={(e) => setAffForm(f => ({ ...f, affiliate_type: e.target.checked ? 'both' : 'padrão' }))}
                    className="mt-0.5 w-4 h-4 accent-[#C5A059]"
                    data-testid="aff-is-master"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                      <Crown className="w-3.5 h-3.5 text-[#C5A059]" />
                      Esta pessoa também recruta outros afiliados
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                      Marcando, ela vira <strong className="text-[#C5A059]">Padrão + Master</strong>: recebe comissão das próprias vendas E também ganha bônus quando os afiliados que ela recrutar venderem.
                    </p>
                  </div>
                </label>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowAffForm(false)} className="h-9 text-sm border-white/10 text-slate-300 hover:text-white">Cancelar</Button>
                <Button onClick={handleCreateAffiliate} disabled={creatingAff}
                  className="h-9 text-sm bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold"
                  data-testid="aff-submit-btn">
                  {creatingAff ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cadastrar Afiliado'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Affiliate List */}
        {loadingAffs ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" /></div>
        ) : affiliates.length === 0 ? (
          <Card className="border-0 bg-[#0A2647] shadow-lg">
            <CardContent className="py-12 text-center">
              <Users className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 text-sm">Nenhum afiliado cadastrado ainda.</p>
              <p className="text-slate-500 text-xs mt-1">Clique em "Cadastrar Afiliado" para começar a montar sua equipe de vendas!</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 bg-[#0A2647] shadow-lg" data-testid="partner-affiliates-table">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Afiliado</th>
                      <th className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">Tipo</th>
                      <th className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">Vendas</th>
                      <th className="text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">Status</th>
                      <th className="text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">Link de Vendas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {affiliates.map(aff => (
                      <tr key={aff.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`aff-row-${aff.id}`}>
                        <td className="px-5 py-3">
                          <p className="text-white text-sm font-medium">{aff.name}</p>
                          <p className="text-slate-500 text-[10px]">{aff.email}</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            aff.affiliate_type === 'master' || aff.affiliate_type === 'both'
                              ? 'bg-[#C5A059]/20 text-[#C5A059]'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {aff.affiliate_type === 'both' ? 'Padrão+Master' : (aff.affiliate_type === 'master' ? 'Master' : 'Padrão')}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className="text-white text-sm font-bold">{aff.referral_count || 0}</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            aff.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {aff.status === 'approved' ? 'Ativo' : 'Pendente'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-400 font-mono truncate max-w-[160px]">/vendedor/{aff.slug}</span>
                            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/vendedor/${aff.slug}`); toast.success('Link copiado!'); }}
                              title="Copiar link de vendas do afiliado"
                              className="p-1 hover:bg-white/10 rounded text-slate-400 hover:text-[#C5A059]">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info: commission table */}
        <Card className="border-0 bg-[#0A2647] shadow-lg">
          <CardContent className="p-5">
            <div className="flex items-start gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Info className="w-4 h-4 text-[#C5A059]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-sm mb-2">Como funciona a comissão?</h3>
                <div className="space-y-1.5 text-[11px] text-slate-400 leading-relaxed">
                  <p><span className="text-white font-semibold">Afiliado Padrão:</span> Recebe comissão fixa por implantação (R$50 a R$250 conforme o plano)</p>
                  <p><span className="text-white font-semibold">Afiliado Master:</span> Recebe comissão própria + R$50 a R$100 por cada venda dos afiliados da rede dele (conforme o plano)</p>
                  <p><span className="text-[#C5A059] font-semibold">Você (Parceiro):</span> Recebe {partner.commission_rate}% recorrente mensal APOS os custos de implantação serem cobertos pelas mensalidades do cliente</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      )}

      {activeTab === 'implantar' && (
        <>
          <PartnerImplantClientsTab token={token} />
          <div className="max-w-6xl mx-auto px-3 sm:px-6 pb-6">
            <BatchImplantCard
              batchUrl={`${API}/partners/panel/${token}/implant-batch`}
              statusUrl={(id) => `${API}/partners/panel/${token}/implant-batch/${id}`}
            />
          </div>
        </>
      )}

      {activeTab === 'patrocinios' && (
        <PartnerSponsorshipsTab token={token} partnerName={partner.name} initialView={initialView} />
      )}

      {activeTab === 'guia' && (
        <main className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <PartnerGuide
            partnerName={partner.name}
            partnerSlug={partner.slug}
            partnerToken={token}
            isGeneric={false}
          />
        </main>
      )}

      {/* Footer */}
      <footer className="mt-12 border-t border-white/5 py-6 text-center">
        <p className="text-[10px] text-slate-500">
          FaloComODono - Painel Exclusivo do Parceiro | {partner.name}
        </p>
      </footer>
      <HelpButton ambiente="parceiro" tela={activeTab} />
    </div>
  );
};

export default PartnerPanel;
